import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr } from '@utils';
import { IVehicleQuestionCommonProps } from '@models';
import { FinancingType, FleetVehicle } from '@services';
import { Grid, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FormikNumber, FormikRadio, FormikTextfield } from '@components/common';
import { FormStepGreySection, FormStepLayout, FormStepQuestion } from '@components/portal/common';

type FormFields = Partial<FleetVehicle>;
const translationPath = (key: string) => `vehicle-list.add-edit-vehicle.question-vehicle-finance.${key}`;
const getFormFieldValues = (values?: FormFields): FormFields => ({
	financingType: values?.financingType,
	registrationDate: values?.registrationDate || new Date(),
	leasingDistance: values?.leasingDistance,
	leasingDuration: values?.leasingDuration,
	leasingStartDate: values?.leasingStartDate || new Date(),
	otherDetails: values?.otherDetails
});

export interface IQuestionVehicleFinanceProps extends IVehicleQuestionCommonProps<FormFields> {}

export const QuestionVehicleFinance: FC<IQuestionVehicleFinanceProps> = ({ values, onBack, onNext, ...props }) => {
	const { t } = useTranslation('portal');
	const [initialValues, setInitialValues] = useState<FormFields>(getFormFieldValues(values));
	useEffect(() => setInitialValues(getFormFieldValues(values)), [values]);

	const formik = useFormik<FormFields>({
		initialValues,
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			financingType: yup.string().required(t('field-required')),
			registrationDate: yup.string().when('financingType', {
				is: FinancingType.CASH,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable()
			}),
			leasingStartDate: yup.string().when('financingType', {
				is: FinancingType.RENTAL,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable()
			}),
			leasingDuration: yup.number().when('financingType', {
				is: FinancingType.RENTAL,
				then: yup.number().required(t('field-required')),
				otherwise: yup.number().nullable()
			}),
			leasingDistance: yup.number().when('financingType', {
				is: FinancingType.RENTAL,
				then: yup.number().required(t('field-required')),
				otherwise: yup.number().nullable()
			}),
			otherDetails: yup.string().when('financingType', {
				is: FinancingType.OTHER,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable()
			})
		}),
		onSubmit: ({ financingType, registrationDate, leasingDistance, leasingDuration, leasingStartDate, otherDetails }, api) => {
			if (financingType === FinancingType.CASH) {
				onNext?.({
					financingType,
					registrationDate,
					leasingDistance: null,
					leasingDuration: null,
					leasingStartDate: null,
					otherDetails: null
				});
			} else if (financingType === FinancingType.RENTAL) {
				onNext?.({ financingType, registrationDate: null, leasingDistance, leasingDuration, leasingStartDate, otherDetails: null });
			} else if (financingType === FinancingType.OTHER) {
				onNext?.({
					financingType,
					registrationDate: null,
					leasingDistance: null,
					leasingDuration: null,
					leasingStartDate: null,
					otherDetails
				});
			}
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	return (
		<FormStepLayout
			title={<Tr.Portal path={translationPath('title')} />}
			primaryButtonProps={{ disabled, onClick: submitForm, 'data-testid': 'add-edit-vehicle-next-button' }}
			secondaryButtonProps={{ onClick: onBack, 'data-testid': 'add-edit-vehicle-back-button' }}
			{...props}
		>
			<form onSubmit={handleSubmit}>
				<Stack direction="column" sx={{ width: 1.0 }}>
					<FormStepQuestion title={<Tr.Portal path={translationPath('first-question')} />}>
						<Grid container>
							<Grid item xs={6}>
								<FormikRadio
									formikApi={formik}
									name="financingType"
									value={FinancingType.CASH}
									formControlLabelProps={{ label: <Tr.Portal path={translationPath('cash-purchase')} /> }}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikRadio
									formikApi={formik}
									name="financingType"
									value={FinancingType.RENTAL}
									formControlLabelProps={{ label: <Tr.Portal path={translationPath('long-term-rental')} /> }}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikRadio
									formikApi={formik}
									name="financingType"
									value={FinancingType.OTHER}
									formControlLabelProps={{ label: <Tr.Portal path="other" /> }}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					{formik.values.financingType === FinancingType.CASH ? (
						<FormStepGreySection>
							<FormStepQuestion title={<Tr.Portal path={translationPath('second-question-for-cash')} />}>
								<Grid container>
									<Grid item xs={6}>
										<DatePicker
											value={formik.values.registrationDate}
											onChange={(value) => formik.setFieldValue('registrationDate', value, true)}
											OpenPickerButtonProps={{
												color: 'primary'
											}}
											renderInput={({ error: _, ...params }) => (
												<FormikTextfield
													formikApi={formik}
													name="registrationDate"
													title={t(translationPath('registration-date'))}
													placeholder={t(translationPath('registration-date'))}
													{...params}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</FormStepQuestion>
						</FormStepGreySection>
					) : null}

					{formik.values.financingType === FinancingType.RENTAL ? (
						<FormStepGreySection>
							<FormStepQuestion title={<Tr.Portal path={translationPath('second-question-for-rental')} />}>
								<Grid container rowSpacing={3} columnSpacing={4}>
									<Grid item xs={6}>
										<DatePicker
											value={formik.values.leasingStartDate}
											onChange={(value) => formik.setFieldValue('leasingStartDate', value, true)}
											OpenPickerButtonProps={{ color: 'primary' }}
											renderInput={({ error: _, ...params }) => (
												<FormikTextfield
													formikApi={formik}
													name="leasingStartDate"
													title={t(translationPath('leasing-start-date'))}
													placeholder={t(translationPath('leasing-start-date'))}
													{...params}
												/>
											)}
										/>
									</Grid>
									<Grid item xs={6}>
										<FormikNumber
											formikApi={formik}
											name="leasingDuration"
											title={t(translationPath('leasing-duration'))}
											placeholder={t(translationPath('leasing-duration'))}
										/>
									</Grid>
									<Grid item xs={6}>
										<FormikNumber
											formikApi={formik}
											name="leasingDistance"
											title={t(translationPath('leasing-distance'))}
											placeholder={t(translationPath('leasing-distance'))}
										/>
									</Grid>
								</Grid>
							</FormStepQuestion>
						</FormStepGreySection>
					) : null}

					{formik.values.financingType === FinancingType.OTHER ? (
						<FormStepGreySection>
							<FormStepQuestion title={<Tr.Portal path={translationPath('second-question-for-other')} />}>
								<Grid container>
									<Grid item xs={6}>
										<FormikTextfield
											formikApi={formik}
											name="otherDetails"
											title={t(translationPath('insert-financel-method'))}
											placeholder={t(translationPath('insert-financel-method'))}
										/>
									</Grid>
								</Grid>
							</FormStepQuestion>
						</FormStepGreySection>
					) : null}
				</Stack>
			</form>
		</FormStepLayout>
	);
};
